import { ref, computed } from "vue";

export interface UserInfo {
  email: string;
  name: string;
  /**
   * Google profile picture URL from the id_token `picture` claim.
   * May be `undefined` if the user didn't grant the `profile` scope,
   * if Google omitted the claim, or if the claim wasn't a non-empty
   * string (server-side `stringClaim` filter). The server does NOT
   * validate URL shape — malformed URLs still reach the client; the
   * Sidebar's `<img onerror>` falls back to an email-initial circle
   * in that case.
   */
  picture?: string;
}

/**
 * Module-level state so all components share one source of truth.
 * `ready` indicates whether we've completed the initial /api/auth/me probe;
 * the layout shouldn't render its body until then to avoid a flash of the
 * wrong route (login flashing before the redirect to home, or vice versa).
 */
const user = ref<UserInfo | null>(null);
const ready = ref(false);

export function useAuth() {
  return {
    user,
    ready,
    isAuthenticated: computed(() => user.value !== null),
    refresh,
    signOut,
    /** Test-only: reset module state between tests. */
    _resetForTests,
  };
}

async function refresh(): Promise<void> {
  try {
    const resp = await fetch("/api/auth/me", { credentials: "include" });
    user.value = resp.ok ? ((await resp.json()) as UserInfo) : null;
  } catch {
    user.value = null;
  } finally {
    ready.value = true;
  }
}

/**
 * Event name dispatched on `window` after `signOut()` completes. App.vue
 * listens for this to route the user back to /login without needing
 * useAuth to depend on vue-router (avoids a circular dep with router.ts).
 */
export const SIGNED_OUT_EVENT = "segment-web:signed-out";

async function signOut(): Promise<void> {
  try {
    await fetch("/api/auth/logout", { method: "POST", credentials: "include" });
  } catch {
    // Best-effort: even if the network call fails, clear local state below.
  }
  user.value = null;
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(SIGNED_OUT_EVENT));
  }
}

function _resetForTests(): void {
  user.value = null;
  ready.value = false;
}
